import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Popper,
    Paper,
    Grid,
    IconButton,
    Stack,
    ClickAwayListener,
    useMediaQuery,
    useTheme,
    InputAdornment,
} from '@mui/material';
import { DateRange } from 'react-date-range';
import { format } from 'date-fns';
import { Close, CalendarToday } from '@mui/icons-material';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import '../../assets/styles/DateRangePicker.scss';
import moment from 'moment';

// Auto-inserts '/' and ' - ' as user types digits: DD/MM/YYYY - DD/MM/YYYY
const autoFormatDateRange = (rawValue) => {
    const digits = rawValue.replace(/\D/g, '').slice(0, 16);
    let result = '';
    for (let i = 0; i < digits.length; i++) {
        if (i === 2 || i === 4) result += '/';
        else if (i === 8) result += ' - ';
        else if (i === 10 || i === 12) result += '/';
        result += digits[i];
    }
    return result;
};

const parseDMY = (str) => {
    const trimmed = (str || '').trim();
    const parts = trimmed.split('/');
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts;
    if (!dd || !mm || !yyyy || yyyy.length !== 4) return null;
    const d = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
    if (isNaN(d.getTime()) || d.getDate() !== parseInt(dd)) return null;
    return d;
};

export default function CommonDateRangePicker({ startDate, endDate, onChange, label = 'Date Range' }) {
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedQuickFilter, setSelectedQuickFilter] = useState(null);
    const [inputValue, setInputValue] = useState('');

    const formatDateRange = () => {
        if (!startDate || !endDate) return '';
        return `${format(new Date(startDate), 'dd/MM/yyyy')} - ${format(new Date(endDate), 'dd/MM/yyyy')}`;
    };

    // Sync input value when dates change externally (calendar pick, quick filter, clear)
    useEffect(() => {
        setInputValue(formatDateRange());
    }, [startDate, endDate]);

    const handleCalendarClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);

        // Determine selected quick filter on open
        if (startDate && endDate) {
            const currentStart = moment(startDate);
            const currentEnd = moment(endDate);
            let matchedIndex = null;

            quickFilters.forEach((preset, index) => {
                if (preset.getRange) {
                    const range = preset.getRange();
                    const presetStart = moment(range.startDate);
                    const presetEnd = moment(range.endDate);
                    if (currentStart.isSame(presetStart, 'day') && currentEnd.isSame(presetEnd, 'day')) {
                        matchedIndex = index;
                    }
                }
            });
            setSelectedQuickFilter(matchedIndex);
        } else {
            setSelectedQuickFilter(null);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleInputChange = (e) => {
        setInputValue(autoFormatDateRange(e.target.value));
    };

    const parseAndApply = (value) => {
        const trimmed = (value || '').trim();
        if (trimmed === '') {
            onChange({ startDate: null, endDate: null });
            return true;
        }
        const parts = trimmed.split(' - ');
        if (parts.length === 2) {
            const start = parseDMY(parts[0]);
            const end = parseDMY(parts[1]);
            if (start && end && end >= start) {
                onChange({ startDate: start, endDate: end });
                return true;
            }
        }
        return false;
    };

    const handleInputBlur = () => {
        if (!parseAndApply(inputValue)) {
            // Revert to last valid value
            setInputValue(formatDateRange());
        }
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (!parseAndApply(inputValue)) {
                setInputValue(formatDateRange());
            }
            e.target.blur();
        }
    };

    const quickFilters = [
        {
            label: 'Today',
            getRange: () => ({
                startDate: moment().startOf('day').toDate(),
                endDate: moment().endOf('day').toDate(),
            })
        },
        {
            label: 'Yesterday',
            getRange: () => ({
                startDate: moment().subtract(1, 'day').startOf('day').toDate(),
                endDate: moment().subtract(1, 'day').endOf('day').toDate(),
            })
        },
        {
            label: 'Last 7 Days',
            getRange: () => ({
                startDate: moment().subtract(6, 'days').startOf('day').toDate(),
                endDate: moment().endOf('day').toDate(),
            })
        },
        {
            label: 'Last 30 Days',
            getRange: () => ({
                startDate: moment().subtract(29, 'days').startOf('day').toDate(),
                endDate: moment().endOf('day').toDate(),
            })
        },
        {
            label: 'This Month',
            getRange: () => ({
                startDate: moment().startOf('month').toDate(),
                endDate: moment().endOf('month').toDate(),
            })
        },
        {
            label: 'Last Month',
            getRange: () => ({
                startDate: moment().subtract(1, 'month').startOf('month').toDate(),
                endDate: moment().subtract(1, 'month').endOf('month').toDate(),
            })
        },
        {
            label: 'Custom Range',
            getRange: null,
        },
    ];

    const handleQuickFilter = (preset, index) => {
        if (preset.label === 'Custom Range') {
            setSelectedQuickFilter(index);
            return;
        }
        const range = preset.getRange();
        setSelectedQuickFilter(index);
        onChange({
            startDate: range.startDate,
            endDate: range.endDate
        });
        setAnchorEl(null);
    };

    const handleManualDateChange = (ranges) => {
        const { startDate: newStart, endDate: newEnd } = ranges.selection;
        onChange({ startDate: newStart, endDate: newEnd });
        setSelectedQuickFilter(null);
    };

    const handleClearDateRange = () => {
        onChange({ startDate: null, endDate: null });
        handleClose();
    };

    return (
        <Box className="date-picker-wrapper" sx={{ width: '100%' }}>
            <TextField
                label={label}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                size="small"
                placeholder="DD/MM/YYYY - DD/MM/YYYY"
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ gap: 0 }}>
                            {startDate && endDate && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClearDateRange();
                                    }}
                                >
                                    <Close fontSize="small" />
                                </IconButton>
                            )}
                            <IconButton
                                size="small"
                                onClick={handleCalendarClick}
                                sx={{ mr: -1 }}
                            >
                                <CalendarToday fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
                sx={{ width: '100%' }}
            />
            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{
                    zIndex: 1500,
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { xs: '100vw', sm: '850px' },
                    left: { xs: 0, sm: 'auto' },
                    right: { xs: 0, sm: 'auto' },
                    px: { xs: 1, sm: 0 }
                }}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 0,
                            background: '#fff',
                            width: { xs: '100%', sm: '520px' },
                            maxWidth: { xs: '100%', sm: '520px' },
                            borderRadius: '12px',
                            overflow: 'hidden',
                            display: 'flex',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            border: '1px solid #f0f0f0',
                            flexDirection: { xs: 'column', md: 'row' }
                        }}
                    >
                        <Grid container sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                            {/* Left Side - Quick Filter Buttons (Vertical) */}
                            <Grid item xs={12} md={3} sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                borderRight: { xs: 'none', md: '1px solid #f0f0f0' },
                                borderBottom: { xs: '1px solid #f0f0f0', md: 'none' }
                            }}>
                                <Box sx={{
                                    p: 1.5,
                                    backgroundColor: '#fafafa',
                                    width: '100%',
                                    height: '100%'
                                }}>
                                    <Box sx={{ mb: 1.5, fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Quick Filters
                                    </Box>
                                    <Stack direction={{ xs: 'row', md: 'column' }} spacing={0.5} sx={{
                                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                                        overflowX: { xs: 'auto', md: 'visible' }
                                    }}>
                                        {quickFilters.map((preset, index) => (
                                            <Button
                                                key={preset.label}
                                                variant={selectedQuickFilter === index ? "contained" : "text"}
                                                size="small"
                                                onClick={() => handleQuickFilter(preset, index)}
                                                fullWidth={false}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontSize: '0.8125rem',
                                                    justifyContent: 'flex-start',
                                                    px: 1.5,
                                                    py: 0.75,
                                                    mb: { xs: 0, md: 0.25 },
                                                    mr: { xs: 0.5, md: 0 },
                                                    minWidth: { xs: 'auto', md: '100%' },
                                                    borderRadius: '4px',
                                                    boxShadow: 'none',
                                                    backgroundColor: selectedQuickFilter === index ? 'primary.main' : 'transparent',
                                                    color: selectedQuickFilter === index ? 'white' : 'text.primary',
                                                    '&:hover': {
                                                        backgroundColor: selectedQuickFilter === index ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)',
                                                        boxShadow: 'none',
                                                    }
                                                }}
                                                className={`quick-filter-btn ${selectedQuickFilter === index ? 'active' : ''}`}
                                            >
                                                {preset.label}
                                            </Button>
                                        ))}
                                    </Stack>
                                </Box>
                            </Grid>

                            {/* Right Side - Calendar */}
                            <Grid item xs={12} md={9} sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}>
                                <Box sx={{
                                    p: { xs: 1, sm: 1.5 },
                                    overflow: 'hidden',
                                    maxWidth: '100%'
                                }}>
                                    <DateRange
                                        onChange={handleManualDateChange}
                                        moveRangeOnFirstSelection={false}
                                        ranges={[{
                                            startDate: startDate || null,
                                            endDate: endDate || null,
                                            key: 'selection'
                                        }]}
                                        months={1}
                                        direction="horizontal"
                                        showDateDisplay={false}
                                        showPreview={true}
                                        rangeColors={['#1976d2']}
                                    />

                                    {/* Action Buttons */}
                                    <Box display="flex" justifyContent="flex-end" gap={1} sx={{
                                        mt: 1,
                                        pt: 1.5,
                                        borderTop: '1px solid #f0f0f0',
                                        flexWrap: 'wrap'
                                    }}>
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => {
                                                handleClose();
                                            }}
                                            sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8125rem' }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => {
                                                handleClose();
                                            }}
                                            sx={{ textTransform: 'none', px: 2, borderRadius: '4px', boxShadow: 'none', '&:hover': { boxShadow: 'none' }, fontSize: '0.8125rem' }}
                                        >
                                            Apply
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
}
